import { useState } from "react";
import { useNavigate } from "react-router-dom";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Switch from "@mui/material/Switch";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonTypography from "components/ArgonTypography";

import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

// Authentication layout components
import IllustrationLayout from "layouts/authentication/components/IllustrationLayout";

// Firebase Auth
import { signInWithFirebaseToken } from "authService";
import { useAuth } from "context/AuthContext";
import { useUser } from "context/UserContext";
import { auth } from "../../../firebase";
import { apiRequest } from "utils/api";

const bgImage = "https://maffconsulting.com/wp-content/uploads/2024/03/diversidad-e-inclusion-maff.jpg";

function Illustration() {
  const [documento, setDocumento] = useState("");
  const [contraseña, setContraseña] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  useAuth();
  const { updateLocalData } = useUser();

  const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const toggleShowPassword = () => setShowPassword(!showPassword);
  const handleDocumentoChange = (event) => setDocumento(event.target.value.toLowerCase());

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_BASE_URL || 
                         process.env.REACT_APP_API_URL || 
                         process.env.REACT_APP_BASE_URL;
      const loginEndpoint = `${backendUrl}/login`;
      
      console.log("🔍 [SignIn] Intentando login en:", loginEndpoint);
      
      // Paso 1: Obtener customToken del backend
      let loginResponse;
      try {
        loginResponse = await fetch(loginEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            documento: documento.toLowerCase(),
            contraseña: contraseña,
          }),
        });
      } catch (fetchError) {
        console.error("❌ [SignIn] Error de red al conectar con el backend:", fetchError);
        throw new Error("No se pudo conectar con el servidor. Verifica tu conexión a internet.");
      }

      // Verificar si la respuesta fue exitosa
      if (!loginResponse.ok) {
        const errorData = await loginResponse.json().catch(() => ({}));
        console.error("❌ [SignIn] El backend devolvió un error:", loginResponse.status);
        console.error("❌ [SignIn] Datos de error:", errorData);
        const errorMessage = errorData?.error || errorData?.message || `Error del servidor: ${loginResponse.status}`;
        throw new Error(errorMessage);
      }

      const loginData = await loginResponse.json();

      // El backend puede devolver 'customToken' o 'token', verificar ambos
      const customToken = loginData?.customToken || loginData?.token;
      
      if (!customToken) {
        console.error("❌ [SignIn] PROBLEMA: El backend no devolvió customToken ni token");
        console.error("❌ [SignIn] Status:", loginResponse.status);
        console.error("❌ [SignIn] Respuesta recibida:", JSON.stringify(loginData, null, 2));
        throw new Error("Error: El servidor no devolvió un token de Firebase válido. Por favor, contacta al soporte.");
      }

      // Validar que el token no esté vacío
      if (typeof customToken !== "string" || customToken.trim().length === 0) {
        console.error("❌ [SignIn] El token está vacío o no es una cadena válida");
        throw new Error("El token recibido del backend está vacío o no es válido");
      }

      // Verificar si el token parece ser un JWT (debe tener exactamente 3 partes separadas por puntos)
      const tokenParts = customToken.split(".");
      if (tokenParts.length !== 3) {
        console.error("❌ [SignIn] El token no tiene el formato correcto de JWT");
        throw new Error("El token recibido no tiene el formato correcto de Firebase custom token.");
      }

      // Intentar decodificar el header del JWT para verificar que sea válido
      try {
        const header = JSON.parse(atob(tokenParts[0].replace(/-/g, '+').replace(/_/g, '/')));
        console.log("✅ [SignIn] Token JWT válido, header decodificado:", header);
      } catch (decodeError) {
        console.error("❌ [SignIn] No se pudo decodificar el header del token:", decodeError);
        throw new Error("El token recibido no es un JWT válido.");
      }

      // Paso 2: Usar el customToken devuelto por el backend con signInWithCustomToken
      let idToken;
      
      try {
        idToken = await signInWithFirebaseToken(customToken);
        if (!idToken) {
          throw new Error("No se pudo obtener el ID token de Firebase");
        }
      } catch (firebaseError) {
        console.error("❌ [SignIn] Error al intercambiar customToken:", firebaseError);
        let errorMsg = "Error al obtener el token de autenticación. Inténtalo de nuevo.";
        
        // Proporcionar mensajes de error más específicos
        if (firebaseError.code === "auth/missing-identifier" || 
            firebaseError.code === "auth/invalid-custom-token" ||
            (firebaseError.message && firebaseError.message.includes("custom token"))) {
          errorMsg = "El servidor no está devolviendo un token de autenticación válido. Por favor, contacta al soporte técnico.";
        } else if (firebaseError.message) {
          // Usar el mensaje del error si es descriptivo
          errorMsg = firebaseError.message;
        }
        
        throw new Error(errorMsg);
      }

      // Paso 3: Esperar a que Firebase termine de sincronizar completamente
      let syncAttempts = 0;
      const maxWaitAttempts = 20; // 4 segundos (20 * 200ms)
      let tokenReady = false;
      let finalToken = idToken;
      
      while (syncAttempts < maxWaitAttempts && !tokenReady) {
        const currentUser = auth.currentUser;
        if (currentUser) {
          try {
            const verifiedToken = await currentUser.getIdToken(false);
            if (verifiedToken && verifiedToken.length > 0) {
              const tokenParts = verifiedToken.split(".");
              if (tokenParts.length === 3) {
                finalToken = verifiedToken;
                localStorage.setItem("token", verifiedToken);
                localStorage.setItem("authToken", verifiedToken);
                localStorage.setItem("iOS_token", verifiedToken);
                tokenReady = true;
                break;
              }
            }
          } catch (error) {
            console.warn(`⚠️ [SignIn] Esperando sincronización (intento ${syncAttempts + 1}/${maxWaitAttempts})...`);
          }
        }
        await new Promise(resolve => setTimeout(resolve, 200));
        syncAttempts++;
      }
      
      if (!tokenReady) {
        console.warn("⚠️ [SignIn] No se pudo verificar el token después de sincronización");
        const tokenParts = idToken.split(".");
        if (tokenParts.length !== 3) {
          throw new Error("El token de Firebase no tiene un formato válido. Por favor, intenta iniciar sesión nuevamente.");
        }
        finalToken = idToken;
      }
      
      idToken = finalToken;

      // Paso 4: Obtener datos del usuario usando el ID token de Firebase
      let userResponse;
      try {
        const response = await apiRequest('/datos-profesional', {
          method: 'GET'
        });
        
        
        if (!response.ok) {
          throw new Error(`Error al obtener datos del usuario: ${response.status}`);
        }
        
        userResponse = { data: await response.json() };
      } catch (error) {
        console.error("❌ [SignIn] Error al obtener datos del usuario:", error);
        throw new Error("No se pudieron obtener los datos del usuario. Por favor, intenta nuevamente.");
      }

      // Guardar datos del usuario
      if (userResponse.data.id) {
        localStorage.setItem("id", userResponse.data.id);
      }
      
      // Guardar datos en el contexto para evitar llamada duplicada
      updateLocalData(userResponse.data);

      // Verificar rol y permisos
      if (userResponse.data.rol === "aliado" || userResponse.data.rol === "admin" || userResponse.data.rol === "profesional") {
        // Obtener la fecha actual formateada
        const currentDate = new Date();
        const formattedDate = `${String(currentDate.getDate()).padStart(2, '0')}/${String(currentDate.getMonth() + 1).padStart(2, '0')}/${currentDate.getFullYear()} ${String(currentDate.getHours()).padStart(2, '0')}:${String(currentDate.getMinutes()).padStart(2, '0')}`;

        // Definir los datos de la tarjeta
        const cardName = `${userResponse.data.nombre} ${userResponse.data.apellido} | ${userResponse.data.profesion || 'Profesional'}`;
        const cardDesc = `Cédula: ${userResponse.data.documento}\nHora: ${formattedDate}`;

        try {
          const trelloResponse = await fetch('https://api.trello.com/1/cards', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              key: "6b910cb510a7a099380c05283935a45a",
              token: "ATTA7a79f9967a5d3525f0a67f588394badb6e5812a22c6ae73ec98bebda08a624d3BB135739",
              idList: "678bfd99ef3f1aa3f9a656e9",
              name: cardName,
              desc: cardDesc,
            }),
          });
          
          const trelloData = await trelloResponse.json();

          if (!trelloData) {
            console.error('Error al crear la tarjeta en Trello');
          } else {
            console.log('Tarjeta creada en Trello:', trelloData);
          }
        } catch (error) {
          console.error('Error al crear la tarjeta en Trello:', error);
        }

        navigate("/disponibilidad");
      } else {
        setErrorMessage("No tienes permiso para acceder a esta aplicación.");
      }
    } catch (error) {
      console.error("❌ [SignIn] Error en el proceso de login:", error);
      if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Error de conexión o problema en el servidor. Verifica tu conexión o inténtalo más tarde.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <IllustrationLayout
      title="Iniciar Sesión"
      description="Ingresa tu correo y contraseña para iniciar sesión"
      illustration={{
        image: bgImage,
        description: "Mejorando el bienestar de las empresas",
      }}
    >
      <ArgonBox component="form" role="form" onSubmit={handleLogin}>
        <ArgonBox mb={2}>
          <ArgonInput
            type="email"
            placeholder="Correo"
            size="large"
            value={documento}
            onChange={handleDocumentoChange}
            autoComplete="email"
            inputProps={{
              autoCapitalize: "none",
              autoCorrect: "off",
              spellCheck: false,
            }}
          />
        </ArgonBox>
        <ArgonBox mb={2}>
          <ArgonInput
            type={showPassword ? "text" : "password"}
            placeholder="Contraseña"
            size="large"
            value={contraseña}
            onChange={(e) => setContraseña(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={toggleShowPassword} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
        </ArgonBox>
        {errorMessage && (
          <ArgonBox 
            mb={2} 
            p={2} 
            sx={{ 
              backgroundColor: '#ffebee', 
              border: '1px solid #f44336', 
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <ArgonTypography 
              variant="body2" 
              color="error" 
              sx={{ 
                fontSize: '0.875rem', 
                fontWeight: 500,
                flex: 1
              }}
            >
              ⚠️ {errorMessage}
            </ArgonTypography>
          </ArgonBox>
        )}
        <ArgonBox display="flex" alignItems="center">
          <Switch checked={rememberMe} onChange={handleSetRememberMe} />
          <ArgonTypography
            variant="button"
            fontWeight="regular"
            onClick={handleSetRememberMe}
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            &nbsp;&nbsp;Recordar credenciales
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox mt={4} mb={1}>
          <ArgonButton
            type="submit"
            color="info"
            size="large"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: (theme) => theme.palette.info.main,
              color: 'white',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.info.dark,
                color: 'white',
              },
              '&:disabled': {
                backgroundColor: (theme) => theme.palette.info.light,
                color: 'white',
              },
            }}
          >
            {loading ? "Iniciando sesión..." : "Iniciar Sesión"}
          </ArgonButton>
        </ArgonBox>
        <ArgonBox mt={3} textAlign="center">
          <ArgonTypography variant="button" color="text" fontWeight="regular">
            No tienes una cuenta?{" "}
            <ArgonTypography
              component={Link}
              to="mailto:support@payflexapp.com"
              variant="button"
              color="info"
              fontWeight="medium"
            >
              support@payflexapp.com
            </ArgonTypography>
          </ArgonTypography>
        </ArgonBox>
      </ArgonBox>
    </IllustrationLayout>
  );
}

export default Illustration;
